/**
 * CelebrationSystem — premium multi-layered burst effect.
 *
 * Design pattern, translated from Magic UI / Framer Motion industry idioms
 * into pure Canvas:
 *
 *   Layer 1 — FLASH:     bright white-of-hue radial gradient at impact (~50ms)
 *   Layer 2 — SHOCKWAVE: expanding stroked ring, eased fade-out (~350ms)
 *   Layer 3 — CONFETTI:  pooled mixed-shape particles with rotation +
 *                        gravity + air drag + hue spread (~1.2s)
 *   Layer 4 — SPARKLES:  4-point twinkles spawned with staggered delays,
 *                        each twinkle pops in/out via eased scale (~400ms)
 *
 * Each layer uses a fixed-size pool — allocation-free at runtime.
 *
 * Triggered by the engine on:
 *   - player-credited kills,
 *   - combo ≥4 perfect-eats,
 *   - new personal-best mass milestones.
 */

interface Flash {
  alive: boolean;
  x: number;
  y: number;
  hue: number;
  life: number;
  maxLife: number;
}

interface ShockRing {
  alive: boolean;
  x: number;
  y: number;
  hue: number;
  life: number;
  maxLife: number;
  /** Final radius the ring expands to (world units). */
  maxRadius: number;
}

interface Confetto {
  alive: boolean;
  x: number;
  y: number;
  vx: number;
  vy: number;
  hue: number;
  rot: number;
  rotSpeed: number;
  life: number;
  maxLife: number;
  size: number;
  /** 0=square, 1=triangle, 2=circle */
  shape: 0 | 1 | 2;
}

interface Sparkle {
  alive: boolean;
  x: number;
  y: number;
  hue: number;
  /** Negative spawn-delay = waiting to appear. Positive = age since spawn. */
  spawnDelay: number;
  life: number;
  maxLife: number;
  /** Sparkle outer radius (world units). */
  radius: number;
}

export class CelebrationSystem {
  private readonly flashes: Flash[] = [];
  private readonly rings: ShockRing[] = [];
  private readonly confetti: Confetto[] = [];
  private readonly sparkles: Sparkle[] = [];

  /** Confetti world-space gravity (units/sec²). */
  private readonly gravity = 380;

  constructor() {
    for (let i = 0; i < 6;  i++) this.flashes.push(CelebrationSystem.makeFlash());
    for (let i = 0; i < 8;  i++) this.rings.push(CelebrationSystem.makeRing());
    for (let i = 0; i < 160; i++) this.confetti.push(CelebrationSystem.makeConfetto());
    for (let i = 0; i < 80; i++) this.sparkles.push(CelebrationSystem.makeSparkle());
  }

  private static makeFlash(): Flash {
    return { alive: false, x: 0, y: 0, hue: 0, life: 0, maxLife: 0.05 };
  }
  private static makeRing(): ShockRing {
    return { alive: false, x: 0, y: 0, hue: 0, life: 0, maxLife: 0.35, maxRadius: 220 };
  }
  private static makeConfetto(): Confetto {
    return {
      alive: false, x: 0, y: 0, vx: 0, vy: 0, hue: 0, rot: 0, rotSpeed: 0,
      life: 0, maxLife: 1.2, size: 4, shape: 0,
    };
  }
  private static makeSparkle(): Sparkle {
    return { alive: false, x: 0, y: 0, hue: 0, spawnDelay: 0, life: 0, maxLife: 0.4, radius: 12 };
  }

  /**
   * Trigger a full celebration burst at world (x, y) in hue.
   * `intensity` ∈ [0.5, 2] scales counts + sizes — higher = bigger fanfare.
   */
  trigger(x: number, y: number, hue: number, intensity: number = 1): void {
    const inten = Math.max(0.4, Math.min(2.2, intensity));

    // --- Flash ---
    const flash = this.findFree(this.flashes);
    if (flash) {
      flash.alive = true;
      flash.x = x; flash.y = y; flash.hue = hue;
      flash.maxLife = 0.05;
      flash.life = flash.maxLife;
    }

    // --- Shockwave ring (1 main + sometimes a smaller fast secondary) ---
    const ring = this.findFree(this.rings);
    if (ring) {
      ring.alive = true;
      ring.x = x; ring.y = y; ring.hue = hue;
      ring.maxLife = 0.32;
      ring.life = ring.maxLife;
      ring.maxRadius = 70 * inten;
    }
    if (inten >= 1.3) {
      const ring2 = this.findFree(this.rings);
      if (ring2) {
        ring2.alive = true;
        ring2.x = x; ring2.y = y; ring2.hue = (hue + 30) % 360;
        ring2.maxLife = 0.22;
        ring2.life = ring2.maxLife;
        ring2.maxRadius = 42 * inten;
      }
    }

    // --- Confetti shower — fewer, slower, smaller. ---
    const confettiCount = Math.round(14 * inten);
    const tau = Math.PI * 2;
    for (let i = 0; i < confettiCount; i++) {
      const c = this.findFree(this.confetti);
      if (!c) break;
      const ang = (i / confettiCount) * tau + (Math.random() - 0.5) * 0.4;
      const speed = (95 + Math.random() * 150) * inten;
      const hueJitter = hue + (Math.random() - 0.5) * 60;
      c.alive = true;
      c.x = x; c.y = y;
      c.vx = Math.cos(ang) * speed;
      c.vy = Math.sin(ang) * speed;
      c.hue = hueJitter;
      c.rot = Math.random() * tau;
      c.rotSpeed = (Math.random() - 0.5) * 12;
      c.maxLife = 0.7 + Math.random() * 0.4;
      c.life = c.maxLife;
      c.size = (2 + Math.random() * 2.5) * (0.85 + 0.2 * inten);
      c.shape = (Math.floor(Math.random() * 3) as 0 | 1 | 2);
    }

    // --- Sparkles — fewer + smaller. ---
    const sparkleCount = Math.round(5 * inten);
    for (let i = 0; i < sparkleCount; i++) {
      const s = this.findFree(this.sparkles);
      if (!s) break;
      const ang = Math.random() * tau;
      const dist = (18 + Math.random() * 50) * inten;
      s.alive = true;
      s.x = x + Math.cos(ang) * dist;
      s.y = y + Math.sin(ang) * dist;
      s.hue = hue + (Math.random() - 0.5) * 24;
      s.spawnDelay = i * 0.05 + Math.random() * 0.03;
      s.maxLife = 0.28 + Math.random() * 0.10;
      s.life = s.maxLife;
      s.radius = (6 + Math.random() * 5) * inten;
    }
  }

  step(dt: number): void {
    // Flashes
    for (let i = 0; i < this.flashes.length; i++) {
      const f = this.flashes[i]!;
      if (!f.alive) continue;
      f.life -= dt;
      if (f.life <= 0) f.alive = false;
    }
    // Rings
    for (let i = 0; i < this.rings.length; i++) {
      const r = this.rings[i]!;
      if (!r.alive) continue;
      r.life -= dt;
      if (r.life <= 0) r.alive = false;
    }
    // Confetti — gravity + drag + rotation
    const dragX = Math.exp(-0.7 * dt);
    const dragY = Math.exp(-0.45 * dt);
    for (let i = 0; i < this.confetti.length; i++) {
      const c = this.confetti[i]!;
      if (!c.alive) continue;
      c.x += c.vx * dt;
      c.y += c.vy * dt;
      c.vx *= dragX;
      c.vy = c.vy * dragY + this.gravity * dt;
      c.rot += c.rotSpeed * dt;
      c.life -= dt;
      if (c.life <= 0) c.alive = false;
    }
    // Sparkles — spawn delay then aging
    for (let i = 0; i < this.sparkles.length; i++) {
      const s = this.sparkles[i]!;
      if (!s.alive) continue;
      if (s.spawnDelay > 0) {
        s.spawnDelay -= dt;
      } else {
        s.life -= dt;
        if (s.life <= 0) s.alive = false;
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Iterators for the renderer
  // ---------------------------------------------------------------------------

  forEachFlash(fn: (f: Readonly<Flash>) => void): void { for (const f of this.flashes) if (f.alive) fn(f); }
  forEachRing(fn: (r: Readonly<ShockRing>) => void): void { for (const r of this.rings) if (r.alive) fn(r); }
  forEachConfetto(fn: (c: Readonly<Confetto>) => void): void { for (const c of this.confetti) if (c.alive) fn(c); }
  forEachSparkle(fn: (s: Readonly<Sparkle>) => void): void {
    for (const s of this.sparkles) if (s.alive && s.spawnDelay <= 0) fn(s);
  }

  private findFree<T extends { alive: boolean }>(pool: T[]): T | null {
    for (let i = 0; i < pool.length; i++) {
      if (!pool[i]!.alive) return pool[i]!;
    }
    return null;
  }
}

export type { Flash, ShockRing, Confetto, Sparkle };
